<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

type Props = {
  src: string;
  alt?: string;

  zoom?: number; 

  maxZoom?: number;

  holdDelay?: number; 

  enablePinch?: boolean; 

  enableWheel?: boolean; 
};

const props = withDefaults(defineProps<Props>(), {
  alt: "",
  zoom: 2,
  maxZoom: 4,
  holdDelay: 180,
  enablePinch: false,
  enableWheel: false,
});

const root = ref<HTMLDivElement | null>(null);

const isPointerFine = ref(false);
const isZoomed = ref(false);

const scale = ref(1);
const tx = ref(0);
const ty = ref(0);

let activePointers = new Map<number, PointerEvent>();

let holdTimer: number | null = null;
let holdPointerId: number | null = null;
let holdStart = { x: 0, y: 0 };
let holdMovedTooFar = false;

let lastPan = { x: 0, y: 0 };

let lastDist = 0;
let lastCentroid = { x: 0, y: 0 };

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function getRect() {
  const el = root.value;
  return el ? el.getBoundingClientRect() : null;
}

function clampTranslate() {
  const rect = getRect();
  if (!rect) return;

  if (scale.value <= 1.001) {
    tx.value = 0;
    ty.value = 0;
    return;
  }

  const maxX = (rect.width * (scale.value - 1)) / 2;
  const maxY = (rect.height * (scale.value - 1)) / 2;

  tx.value = clamp(tx.value, -maxX, maxX);
  ty.value = clamp(ty.value, -maxY, maxY);
}

function setZoom(on: boolean, targetScale?: number) {
  isZoomed.value = on;
  scale.value = on ? clamp(targetScale ?? props.zoom, 1, props.maxZoom) : 1;
  tx.value = 0;
  ty.value = 0;
  clampTranslate();
}

function panToClientPoint(clientX: number, clientY: number) {
  const rect = getRect();
  if (!rect) return;

  const x = clientX - rect.left; 
  const y = clientY - rect.top;  
  const nx = (x / rect.width) * 2 - 1;  
  const ny = (y / rect.height) * 2 - 1; 

  const maxX = (rect.width * (scale.value - 1)) / 2;
  const maxY = (rect.height * (scale.value - 1)) / 2;

  tx.value = clamp(-nx * maxX, -maxX, maxX);
  ty.value = clamp(-ny * maxY, -maxY, maxY);
}

function cancelHoldTimer() {
  if (holdTimer !== null) {
    window.clearTimeout(holdTimer);
    holdTimer = null;
  }
  holdPointerId = null;
  holdMovedTooFar = false;
}

function onMouseEnter(e: MouseEvent) {
  if (!isPointerFine.value) return;
  setZoom(true, props.zoom);
  panToClientPoint(e.clientX, e.clientY);
}

function onMouseMove(e: MouseEvent) {
  if (!isPointerFine.value || !isZoomed.value) return;
  panToClientPoint(e.clientX, e.clientY);
}

function onMouseLeave() {
  if (!isPointerFine.value) return;
  setZoom(false);
}

function onWheel(e: WheelEvent) {
  if (!isPointerFine.value || !props.enableWheel) return;
  if (!root.value) return;

  e.preventDefault();

  const step = e.deltaY < 0 ? 0.2 : -0.2;
  const next = clamp(scale.value + step, 1, props.maxZoom);

  if (next > 1 && !isZoomed.value) isZoomed.value = true;
  scale.value = next;

  panToClientPoint(e.clientX, e.clientY);
  clampTranslate();

  if (scale.value <= 1.001) setZoom(false);
}

function onPointerDown(e: PointerEvent) {
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

  activePointers.set(e.pointerId, e);

  if (e.pointerType !== "mouse") {
    if (activePointers.size === 2) {
      cancelHoldTimer();
      if (props.enablePinch) {
        lastDist = getDistance();
        lastCentroid = getCentroid();
      }
      return;
    }

    cancelHoldTimer();
    holdPointerId = e.pointerId;
    holdStart = { x: e.clientX, y: e.clientY };
    holdMovedTooFar = false;

    holdTimer = window.setTimeout(() => {
      if (holdPointerId === e.pointerId && activePointers.size === 1 && !holdMovedTooFar) {
        setZoom(true, props.zoom);
        lastPan = { x: e.clientX, y: e.clientY };
      }
      holdTimer = null;
    }, props.holdDelay);
  } else {
    lastPan = { x: e.clientX, y: e.clientY };
  }
}

function onPointerMove(e: PointerEvent) {
  if (!activePointers.has(e.pointerId)) return;
  activePointers.set(e.pointerId, e);

  if (e.pointerType !== "mouse" && holdPointerId === e.pointerId && !isZoomed.value) {
    const dx = e.clientX - holdStart.x;
    const dy = e.clientY - holdStart.y;
    if (Math.hypot(dx, dy) > 10) {
      holdMovedTooFar = true;
      cancelHoldTimer();
    }
    return;
  }

  if (e.pointerType !== "mouse" && props.enablePinch && activePointers.size === 2) {
    if (!isZoomed.value) isZoomed.value = true;

    const dist = getDistance();
    const centroid = getCentroid();

    if (!lastDist) lastDist = dist;

    const factor = dist / (lastDist || dist);
    scale.value = clamp(scale.value * factor, 1, props.maxZoom);

    tx.value += centroid.x - lastCentroid.x;
    ty.value += centroid.y - lastCentroid.y;

    lastDist = dist;
    lastCentroid = centroid;

    clampTranslate();
    if (scale.value <= 1.001) setZoom(false);
    return;
  }

  if (e.pointerType !== "mouse") {
    if (!isZoomed.value || scale.value <= 1.001) return;
    if (activePointers.size !== 1) return;

    const dx = e.clientX - lastPan.x;
    const dy = e.clientY - lastPan.y;
    lastPan = { x: e.clientX, y: e.clientY };

    tx.value += dx;
    ty.value += dy;
    clampTranslate();
    return;
  }

  if (isPointerFine.value && isZoomed.value && scale.value > 1.001) {
    if ((e.buttons & 1) === 1) {
      const dx = e.clientX - lastPan.x;
      const dy = e.clientY - lastPan.y;
      lastPan = { x: e.clientX, y: e.clientY };

      tx.value += dx;
      ty.value += dy;
      clampTranslate();
    } else {
    }
  }
}

function onPointerUp(e: PointerEvent) {
  activePointers.delete(e.pointerId);

  if (e.pointerType !== "mouse") {
    cancelHoldTimer();

    if (isZoomed.value && activePointers.size === 0) {
      setZoom(false);
    }

    if (activePointers.size < 2) {
      lastDist = 0;
    }
  }
}

function onPointerCancel(e: PointerEvent) {
  activePointers.delete(e.pointerId);
  cancelHoldTimer();
  if (e.pointerType !== "mouse") setZoom(false);
  lastDist = 0;
}

function getCentroid() {
  const pts = Array.from(activePointers.values());
  const cx = pts.reduce((s, p) => s + p.clientX, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.clientY, 0) / pts.length;
  return { x: cx, y: cy };
}

function getDistance() {
  const pts = Array.from(activePointers.values());
  if (pts.length < 2) return 0;
  const [a, b] = pts;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

const transformStyle = computed(() => ({
  transform: `translate(${tx.value}px, ${ty.value}px) scale(${scale.value})`,
  opacity: isZoomed ? 1 : 0,
  padding: isZoomed ? '35px' : 0,
  cursor:
    isPointerFine.value
      ? (isZoomed.value ? "zoom-out" : "zoom-in")
      : (isZoomed.value ? "grabbing" : "default"),
}));

const originalImageStyle = computed(() => ({
    opacity: isZoomed ? 0 : 1
}));

function onResize() {
  clampTranslate();
}

let ro: ResizeObserver | null = null;

onMounted(() => {
  isPointerFine.value = matchMedia("(pointer: fine)").matches;

  const el = root.value;
  if (el) {
    ro = new ResizeObserver(onResize);
    ro.observe(el);
  }
  window.addEventListener("resize", onResize, { passive: true });
});

onBeforeUnmount(() => {
  cancelHoldTimer();
  ro?.disconnect();
  ro = null;
  window.removeEventListener("resize", onResize);
});
</script>

<template>
  <div
    ref="root"
    class="hzi-root"
    @mouseenter="onMouseEnter"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
    @wheel="onWheel"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <img class="hzi-img" :src="src" :alt="alt" draggable="false" :style="originalImageStyle" />
    <img class="hzi-img hzi-img--zoom" :src="src" :alt="alt" :style="transformStyle" draggable="false" />
  </div>
</template>

<style scoped>
.hzi-root {
  position: relative;
  width: 60%;
  height: 100%;
  overflow: hidden;
  touch-action: none; 
  user-select: none;
  background: transparent;
  border: 1px solid #ccc;
}

.hzi-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
}

.hzi-img--zoom {
  opacity: 0;
  transform-origin: center center;
  transition: opacity 120ms ease;
  will-change: transform;
}

.hzi-root:hover .hzi-img--zoom {
}

.hzi-root :deep(.hzi-img--zoom) {
}

</style>

<script lang="ts">

</script>

<style scoped>

</style>
